/**
 * Knowledge Gaps API service.
 *
 * Communicates with the backend knowledge-gap analysis pipeline endpoints
 * at /api/evals/knowledge-gaps/{org_id}/...
 */
import apiClient, { type ApiResponse } from './client';

// ─── Request types ──────────────────────────────────────────────────────────

export interface KGRunTriggerRequest {
  max_traces: number;
  skip_consolidation: boolean;
}

export interface KGConsolidateRequest {
  since?: string;
  reprocess_all: boolean;
}

export interface KGSummariesParams {
  status?: 'active' | 'dismissed' | 'resolved';
  category?: string;
  sort_by?: 'occurrence_count_desc' | 'first_seen_desc';
  include_evidence?: boolean;
  page?: number;
  limit?: number;
}

export interface KGRunsParams {
  page?: number;
  limit?: number;
}

export interface KGRawFindingsParams {
  page?: number;
  limit?: number;
}

// ─── Response types ─────────────────────────────────────────────────────────

export interface KGTriggerResponse {
  run_id: string;
  organization_id: string;
  status: string;
  message: string;
}

export interface KGRunResponse {
  id: string;
  organization_id: string;
  trigger_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  trace_window_start: string | null;
  trace_window_end: string | null;
  traces_fetched: number;
  traces_flagged: number;
  traces_analyzed: number;
  findings_created: number;
  consolidation_status: string | null;
  summaries_created: number;
  error_message: string | null;
  metadata: Record<string, unknown> | null;
  processed_trace_ids: string[] | null;
  started_at: string | null;
  completed_at: string | null;
  last_trace_created_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface KGRunListResponse {
  runs: KGRunResponse[];
  total: number;
}

export interface KGFindingData {
  finding_type: string;
  topic: string;
  evidence: string;
  details: string;
  suggested_action?: string;
}

export interface KGRawFindingResponse {
  id: string;
  organization_id: string;
  run_id: string;
  trace_id: string;
  trace_created_at: string | null;
  finding_data: KGFindingData;
  is_consolidated: boolean;
  created_at: string;
  session_id: string | null;
}

export interface KGRawFindingListResponse {
  findings: KGRawFindingResponse[];
  total: number;
}

export interface KGSummaryResponse {
  id: string;
  organization_id: string;
  topic: string;
  category: string;
  summary: string;
  occurrence_count: number;
  raw_finding_ids: string[] | null;
  findings?: KGRawFindingResponse[] | null;
  suggested_action: string | null;
  status: 'active' | 'dismissed' | 'resolved';
  first_seen_at: string | null;
  last_seen_at: string | null;
  dismissed_at: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface KGSummaryListResponse {
  summaries: KGSummaryResponse[];
  total: number;
}

export interface KGReportCategory {
  category: string;
  count: number;
  total_occurrences: number;
}

export interface KGReportResponse {
  organization_id: string;
  total_active_summaries: number;
  categories: KGReportCategory[];
  latest_run: KGRunResponse | null;
}

// ─── Service ────────────────────────────────────────────────────────────────

const BASE = '/api/evals/knowledge-gaps';

const knowledgeGapsService = {
  /** High-level report with category counts and latest run info. */
  getReport: (orgId: string): Promise<ApiResponse<KGReportResponse>> =>
    apiClient.get<KGReportResponse>(`${BASE}/${orgId}/report`),

  /** Paginated consolidated summaries with optional filters. */
  getSummaries: (
    orgId: string,
    params?: KGSummariesParams,
  ): Promise<ApiResponse<KGSummaryListResponse>> =>
    apiClient.get<KGSummaryListResponse>(`${BASE}/${orgId}/summaries`, params),

  /** Raw findings that contributed to a specific summary. */
  getSummaryFindings: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGRawFindingResponse[]>> =>
    apiClient.get<KGRawFindingResponse[]>(
      `${BASE}/${orgId}/summaries/${summaryId}/findings`,
    ),

  /** Dismiss a summary (sets status to "dismissed"). */
  dismissSummary: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGSummaryResponse>> =>
    apiClient.patch<KGSummaryResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/dismiss`,
    ),

  /** Resolve a summary (sets status to "resolved"). */
  resolveSummary: (
    orgId: string,
    summaryId: string,
  ): Promise<ApiResponse<KGSummaryResponse>> =>
    apiClient.patch<KGSummaryResponse>(
      `${BASE}/${orgId}/summaries/${summaryId}/resolve`,
    ),

  /** Trigger a full analysis pipeline run. */
  triggerRun: (
    orgId: string,
    data: KGRunTriggerRequest,
  ): Promise<ApiResponse<KGTriggerResponse>> =>
    apiClient.post<KGTriggerResponse>(`${BASE}/${orgId}/runs`, data),

  /** Trigger standalone re-consolidation. */
  triggerConsolidation: (
    orgId: string,
    data: KGConsolidateRequest,
  ): Promise<ApiResponse<KGTriggerResponse>> =>
    apiClient.post<KGTriggerResponse>(`${BASE}/${orgId}/consolidate`, data),

  /** Paginated list of pipeline runs. */
  getRuns: (
    orgId: string,
    params?: KGRunsParams,
  ): Promise<ApiResponse<KGRunListResponse>> =>
    apiClient.get<KGRunListResponse>(`${BASE}/${orgId}/runs`, params),

  /** Single run details. */
  getRun: (
    orgId: string,
    runId: string,
  ): Promise<ApiResponse<KGRunResponse>> =>
    apiClient.get<KGRunResponse>(`${BASE}/${orgId}/runs/${runId}`),

  /** Paginated list of all raw findings (debug/audit). */
  getRawFindings: (
    orgId: string,
    params?: KGRawFindingsParams,
  ): Promise<ApiResponse<KGRawFindingListResponse>> =>
    apiClient.get<KGRawFindingListResponse>(
      `${BASE}/${orgId}/raw-findings`,
      params,
    ),
};

export default knowledgeGapsService;
